// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { appStore as store } from '@advisor-app/store/store';

import { type EditPortfolioConfig } from '../hooks';

import { updateSavedPortfolio } from '@advisor-app/store/slices/my-research';

import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';

import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';

import { type SavedPortfolio } from '@shared/data-access/interfaces/redux/my-research-state';

import type { TFunction } from 'i18next';

export interface EditSavedPortfolioProps {
  editPortfolioConfig: EditPortfolioConfig;

  updatedPortfolioName: string;

  positions: PortfolioLoaded[];

  benchmark: string;

  onClose: () => void;

  portfolios?: string[];

  t: TFunction;
}

export const editSavedPortfolio = ({
  editPortfolioConfig,

  updatedPortfolioName,

  positions,

  benchmark,

  onClose,

  portfolios,

  t,
}: EditSavedPortfolioProps) => {
  const { portfolioBenchmark, portfolioDate, portfolioId, portfolioName } =
    editPortfolioConfig;

  /* istanbul ignore if */

  if (
    updatedPortfolioName !== portfolioName &&
    portfolios?.includes(updatedPortfolioName)
  ) {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,

      title: t(
        'research.portfolio-exists',

        "A portfolio with name '{{name}}' already exists",

        {
          ns: 'research',

          name: updatedPortfolioName,
        },
      ),

      content: t(
        'research.enter-different-name',

        'Please enter a different portfolio name.',

        {
          ns: 'research',
        },
      ),

      buttons: {
        confirm: { show: true, text: t('common.ok', 'Ok', { ns: 'common' }) },
      },
    } as ShowModalOptions);

    return;
  }

  showModal({
    ...MODAL_TEMPLATES_OPTIONS(t).EDIT_SAVED_PORTFOLIO,

    preConfirm: () => {
      const params = { portfolio_id: portfolioId };

      const payload = {
        portfolio_id: portfolioId,

        portfolio: updatedPortfolioName || portfolioName,

        benchmark: benchmark || portfolioBenchmark,

        creation_date: portfolioDate,

        holdings: positions,
      } as unknown as SavedPortfolio;

      return store.dispatch(updateSavedPortfolio({ params, payload }));
    },
  } as ShowModalOptions).then(result => {
    /* istanbul ignore else */

    if (result.isConfirmed) {
      showModal({
        content: t('research.save-success', 'The portfolio was updated correctly', {
          ns: 'research',
        }),

        buttons: {
          confirm: { show: true, text: t('common.ok', 'Ok', { ns: 'common' }) },
        },
      } as ShowModalOptions).then(() => onClose?.());
    } else {
      onClose?.();
    }
  });
};
